import { forwardRef, useEffect, useImperativeHandle } from 'react';
import { Controller, useForm } from 'react-hook-form';

import { FormField, Input, Text } from '@revelio/ui';

import type {
  DeliveryFormat,
  S3DeliveryConfig,
} from '../../data-builder.store';
import type { DeliveryFormHandle } from './DeliveryConfigForm.types';
import {
  DEFAULT_REMOTE_DELIVERY_FORMAT,
  DeliveryFormatRadios,
} from './DeliveryFormatRadios';

type S3ConfigFormProps = {
  initialValue: S3DeliveryConfig | null;
  defaultBucket: string | null;
  isRevelioAdmin: boolean;
  onSave: (value: S3DeliveryConfig) => void;
};

type S3FormValues = {
  bucket: string;
  path: string;
  format: DeliveryFormat;
};

export const S3ConfigForm = forwardRef<DeliveryFormHandle, S3ConfigFormProps>(
  function S3ConfigForm(
    { initialValue, defaultBucket, isRevelioAdmin, onSave },
    ref
  ) {
    const {
      control,
      register,
      handleSubmit,
      reset,
      formState: { errors, isDirty },
    } = useForm<S3FormValues>({
      mode: 'onSubmit',
      reValidateMode: 'onChange',
      defaultValues: {
        bucket: initialValue?.bucket ?? defaultBucket ?? '',
        path: initialValue?.path ?? '',
        format: initialValue?.format ?? DEFAULT_REMOTE_DELIVERY_FORMAT,
      },
    });

    useEffect(() => {
      if (isDirty) {
        return;
      }
      reset({
        bucket: initialValue?.bucket ?? defaultBucket ?? '',
        path: initialValue?.path ?? '',
        format: initialValue?.format ?? DEFAULT_REMOTE_DELIVERY_FORMAT,
      });
    }, [defaultBucket, initialValue, isDirty, reset]);

    useImperativeHandle(
      ref,
      () => ({
        submit: () =>
          new Promise((resolve) => {
            void handleSubmit(
              (value) => {
                const bucket = isRevelioAdmin
                  ? value.bucket
                  : (defaultBucket ?? '');
                onSave({
                  bucket,
                  path: value.path,
                  format: value.format,
                });
                resolve(true);
              },
              () => {
                resolve(false);
              }
            )();
          }),
        isDirty: () => isDirty,
      }),
      [defaultBucket, handleSubmit, isDirty, isRevelioAdmin, onSave]
    );

    return (
      <div>
        {!isRevelioAdmin && (
          <Text fontSize="sm" style={{ marginBottom: '16px' }}>
            Bucket location is pre-configured for your account.
          </Text>
        )}
        {isRevelioAdmin && (
          <FormField
            label="Bucket"
            errors={
              errors.bucket?.message !== undefined
                ? [errors.bucket.message]
                : undefined
            }
            style={{ marginBottom: '16px' }}
          >
            <Input
              placeholder="Bucket"
              {...register('bucket', {
                required: 'Bucket is required',
                minLength: {
                  value: 1,
                  message: 'Minimum length should be 1',
                },
              })}
            />
          </FormField>
        )}

        <FormField
          label="Path"
          errors={
            errors.path?.message !== undefined
              ? [errors.path.message]
              : undefined
          }
          style={{ marginBottom: '16px' }}
        >
          <Input
            placeholder="File Prefix (e.g. batchtime/databuilder_output/file)"
            {...register('path', {
              required: 'Path is required',
              minLength: {
                value: 1,
                message: 'Minimum length should be 1',
              },
              pattern: {
                value: /^[a-zA-Z0-9_\-./]+$/,
                message:
                  'Path can only contain letters, numbers, underscores, hyphens, periods, and forward slashes',
              },
            })}
          />
        </FormField>

        <Controller
          control={control}
          name="format"
          rules={{ required: 'Format is required' }}
          render={({ field }) => (
            <DeliveryFormatRadios
              name="data-builder-s3-format"
              value={field.value}
              onChange={field.onChange}
            />
          )}
        />
      </div>
    );
  }
);
